/**
 * Quality hooks for data quality management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { QualityRule, CreateQualityRuleInput, PaginatedResponse } from '@/types';

interface QualityReport {
  id: string;
  dataset_id: string;
  dataset_name?: string;
  status: 'passed' | 'failed' | 'warning';
  score: number;
  checks_run: number;
  checks_passed: number;
  checks_failed: number;
  created_at: string;
  errors?: Array<{
    rule_name: string;
    message: string;
    severity: 'error' | 'warning';
  }>;
}

interface QualityCheckRequest {
  dataset_id: string;
  rule_ids?: string[];
  async_mode?: boolean;
}

// List quality reports
export const useQualityReports = (params?: {
  dataset_id?: string;
  status?: string;
  page?: number;
  limit?: number;
}) => {
  const { dataset_id, status, page = 1, limit = 20 } = params || {};

  return useQuery<PaginatedResponse<QualityReport>>({
    queryKey: ['quality', 'reports', { dataset_id, status, page, limit }],
    queryFn: async () => {
      const searchParams = new URLSearchParams({
        page: page.toString(),
        size: limit.toString(),
      });

      if (dataset_id) {
        searchParams.append('dataset_id', dataset_id);
      }

      if (status) {
        searchParams.append('status', status);
      }

      const response = await api.get(`/quality/reports?${searchParams.toString()}`);
      return response.data;
    },
  });
};

// Get quality report details
export const useQualityReport = (reportId: string) => {
  return useQuery<QualityReport>({
    queryKey: ['quality', 'reports', reportId],
    queryFn: async () => {
      const response = await api.get(`/quality/reports/${reportId}`);
      return response.data;
    },
    enabled: !!reportId,
  });
};

// List quality rules
export const useQualityRules = () => {
  return useQuery<{ data: QualityRule[] }>({
    queryKey: ['quality', 'rules'],
    queryFn: async () => {
      const response = await api.get('/quality/rules');
      return response.data;
    },
  });
};

// Create quality rule
export const useCreateQualityRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rule: CreateQualityRuleInput) => {
      const response = await api.post('/quality/rules', rule);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quality', 'rules'] });
    },
  });
};

// Run quality check
export const useRunQualityCheck = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: QualityCheckRequest) => {
      const response = await api.post('/quality/checks', request);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quality', 'reports'] });
    },
  });
};

// Update quality rule
export const useUpdateQualityRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & Partial<CreateQualityRuleInput>) => {
      const response = await api.patch(`/quality/rules/${id}`, updates);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quality', 'rules'] });
    },
  });
};

// Delete quality rule
export const useDeleteQualityRule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/quality/rules/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quality', 'rules'] });
    },
  });
};
